//----------------------------------------------------
// ProjectleiderLayout.jsx — Logboek24
//----------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { Bell, LogOut, Menu, Settings, X } from "lucide-react";
import { useUser } from "../context/UserContext";
import { getFireSafetyModuleSettings } from "../services/fireSafetyLogbookApi";
import usePolling from "../hooks/usePolling";
import api from "../services/api";
import { fetchGovernanceStructure, findGovernanceLayer } from "../services/governanceService";
import TopMegaMenu from "../components/navigation/TopMegaMenu";
import WorkspaceCompactIconMenu from "../components/navigation/WorkspaceCompactIconMenu";
import WorkspaceSideMenu from "../components/navigation/WorkspaceSideMenu";
import {
  buildWorkspaceNavigation,
  resolveActiveNavigationItem,
} from "../navigation/navigationConfig";
import {
  getStoredNavigationLayout,
  NAVIGATION_LAYOUT_CHANGE_EVENT,
} from "../navigation/navigationLayout";

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function countUnreadNotifications(items) {
  return (Array.isArray(items) ? items : []).reduce(
    (sum, item) => sum + (item?.readAt ? 0 : 1),
    0
  );
}

function areNotificationsEqual(a, b) {
  const left = Array.isArray(a) ? a : [];
  const right = Array.isArray(b) ? b : [];
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i += 1) {
    const la = left[i] || {};
    const rb = right[i] || {};
    if (String(la?._id || "") !== String(rb?._id || "")) return false;
    if (String(la?.readAt || "") !== String(rb?.readAt || "")) return false;
    if (String(la?.createdAt || "") !== String(rb?.createdAt || "")) return false;
  }
  return true;
}

function areCounterSignalsEqual(prev, next) {
  return (
    Number(prev?.procurementOpen || 0) === Number(next?.procurementOpen || 0) &&
    Number(prev?.externalDeploymentOpen || 0) === Number(next?.externalDeploymentOpen || 0) &&
    Number(prev?.workordersReady || 0) === Number(next?.workordersReady || 0) &&
    Number(prev?.planningOpen || 0) === Number(next?.planningOpen || 0) &&
    Number(prev?.leavePending || 0) === Number(next?.leavePending || 0) &&
    Number(prev?.notificationsUnread || 0) === Number(next?.notificationsUnread || 0)
  );
}

const SIDEBAR_NAVIGATION_LAYOUTS = new Set(["sideMenu", "compactIconMenu"]);

export default function ProjectleiderLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useUser();

  const [fireSafetyModuleEnabled, setFireSafetyModuleEnabled] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationSavingId, setNotificationSavingId] = useState("");
  const [notificationError, setNotificationError] = useState("");
  const [legacyGroupKey, setLegacyGroupKey] = useState("planning");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navigationLayout, setNavigationLayout] = useState(getStoredNavigationLayout);
  const [governance, setGovernance] = useState(null);
  const [counters, setCounters] = useState({
    procurementOpen: 0,
    externalDeploymentOpen: 0,
    workordersReady: 0,
    planningOpen: 0,
    leavePending: 0,
    notificationsUnread: 0,
  });
  const [notificationItems, setNotificationItems] = useState([]);

  const notificationRootRef = useRef(null);

  const tenantLayer = useMemo(
    () => findGovernanceLayer(governance, "tenant"),
    [governance]
  );
  const operationalLayer = useMemo(
    () => findGovernanceLayer(governance, "operational"),
    [governance]
  );
  const visibleLayerKeys = useMemo(
    () =>
      Array.isArray(governance?.roleContext?.visibleLayerKeys)
        ? governance.roleContext.visibleLayerKeys
        : [],
    [governance]
  );
  const tenantLayerVisible =
    visibleLayerKeys.length === 0 || visibleLayerKeys.includes("tenant");

  const breadcrumbs = useMemo(
    () => buildPlatformBreadcrumbs(location.pathname),
    [location.pathname]
  );

  const navigationGroups = useMemo(() => {
    const groups = buildWorkspaceNavigation({
      user,
      fireSafetyModuleEnabled,
      tenantLayerVisible,
      workspaceCounters: counters,
    });

    return groups.map((group) => {
      if (group.key === "planning" && operationalLayer?.description) {
        return {
          ...group,
          description: String(operationalLayer.description).trim() || group.description,
        };
      }
      if (group.key === "beheer" && tenantLayer?.description) {
        return {
          ...group,
          description: String(tenantLayer.description).trim() || group.description,
        };
      }
      return group;
    });
  }, [
    counters,
    fireSafetyModuleEnabled,
    operationalLayer?.description,
    tenantLayer?.description,
    tenantLayerVisible,
    user,
  ]);

  const activeGroupKeyFromLocation = useMemo(() => {
    let best = { key: "", score: -1 };
    for (const group of navigationGroups) {
      const match = resolveActiveNavigationItem(group.items || [], location);
      if (match.score > best.score) {
        best = { key: group.key, score: match.score };
      }
    }
    return best.key;
  }, [location, navigationGroups]);

  useEffect(() => {
    if (!activeGroupKeyFromLocation) return;
    setLegacyGroupKey(activeGroupKeyFromLocation);
  }, [activeGroupKeyFromLocation]);

  useEffect(() => {
    let alive = true;
    fetchGovernanceStructure()
      .then((payload) => {
        if (alive) setGovernance(payload);
      })
      .catch(() => {
        if (alive) setGovernance(null);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function syncNavigationLayout(event) {
      if (event?.type === NAVIGATION_LAYOUT_CHANGE_EVENT) {
        setNavigationLayout(getStoredNavigationLayout());
        return;
      }

      if (event?.storageArea === window.localStorage && event?.key) {
        setNavigationLayout(getStoredNavigationLayout());
      }
    }

    window.addEventListener(NAVIGATION_LAYOUT_CHANGE_EVENT, syncNavigationLayout);
    window.addEventListener("storage", syncNavigationLayout);
    return () => {
      window.removeEventListener(NAVIGATION_LAYOUT_CHANGE_EVENT, syncNavigationLayout);
      window.removeEventListener("storage", syncNavigationLayout);
    };
  }, []);

  const activeGroup =
    navigationGroups.find((group) => group.key === legacyGroupKey) || navigationGroups[0];
  const activeGroupDescription = String(activeGroup?.description || "").trim();
  const activeSubItemTo = useMemo(
    () => resolveActiveNavigationItem(activeGroup?.items || [], location).to,
    [activeGroup, location]
  );
  const sidebarNavigationLayout = SIDEBAR_NAVIGATION_LAYOUTS.has(navigationLayout);
  const workspaceTitle = location.pathname.startsWith("/tenant-admin")
    ? "Bedrijfsinrichting"
    : "Projectleider Cockpit";

  useEffect(() => {
    let active = true;

    async function loadModuleState() {
      try {
        const state = await getFireSafetyModuleSettings();
        if (active) setFireSafetyModuleEnabled(Boolean(state.enabled));
      } catch {
        if (active) setFireSafetyModuleEnabled(false);
      }
    }

    loadModuleState();
    return () => {
      active = false;
    };
  }, []);

  const loadSignals = useCallback(async () => {
    try {
      const [
        procurementRes,
        externalDeploymentRes,
        workordersReadyRes,
        planningOpenRes,
        leavePendingRes,
        notificationsRes,
      ] = await Promise.all([
        api.get("/procurement-orders", {
          params: {
            status: "requested,under_review,approved,ordered",
            limit: 1,
          },
        }),
        api.get("/external-deployments"),
        api.get("/werkbonnen", { params: { status: "gereed_voor_planning" } }),
        api.get("/werkbonnen", { params: { unplanned: "1" } }),
        api.get("/leave-requests", { params: { status: "pending" } }),
        api.get("/notifications/mine", { params: { limit: 8 } }),
      ]);

      const procurementOpen = Number(
        procurementRes?.data?.pagination?.total ||
          (Array.isArray(procurementRes?.data?.items)
            ? procurementRes.data.items.length
            : 0)
      );
      const workordersReady = Array.isArray(workordersReadyRes?.data)
        ? workordersReadyRes.data.length
        : 0;
      const externalItems = Array.isArray(externalDeploymentRes?.data?.items)
        ? externalDeploymentRes.data.items
        : [];
      const externalDeploymentOpen = externalItems.filter((item) => {
        const status = String(item?.status || "").trim().toLowerCase();
        return !["closed", "cancelled", "invoiced"].includes(status);
      }).length;
      const planningOpen = Array.isArray(planningOpenRes?.data)
        ? planningOpenRes.data.length
        : 0;
      const leavePending = Array.isArray(leavePendingRes?.data)
        ? leavePendingRes.data.length
        : 0;

      const notificationsUnreadRaw = Number(notificationsRes?.data?.unreadCount || 0);
      const items = Array.isArray(notificationsRes?.data?.items)
        ? notificationsRes.data.items
        : [];

      const computedUnread = countUnreadNotifications(items);
      const notificationsUnread = Number.isFinite(notificationsUnreadRaw)
        ? notificationsUnreadRaw
        : computedUnread;

      const nextCounters = {
        procurementOpen: Number.isFinite(procurementOpen) ? procurementOpen : 0,
        externalDeploymentOpen: Number.isFinite(externalDeploymentOpen) ? externalDeploymentOpen : 0,
        workordersReady,
        planningOpen,
        leavePending,
        notificationsUnread,
      };

      setCounters((prev) =>
        areCounterSignalsEqual(prev, nextCounters) ? prev : nextCounters
      );
      setNotificationItems((prev) =>
        areNotificationsEqual(prev, items) ? prev : items
      );
      setNotificationError("");
    } catch {
      setNotificationError("Meldingen laden mislukt");
      setCounters((prev) =>
        Number(prev?.notificationsUnread || 0) === 0
          ? prev
          : {
              ...prev,
              notificationsUnread: 0,
            }
      );
      setNotificationItems((prev) => (prev.length === 0 ? prev : []));
    }
  }, []);

  usePolling(loadSignals, 60000, [loadSignals]);

  useEffect(() => {
    if (!notificationOpen) return undefined;

    function handlePointerDown(event) {
      if (!notificationRootRef.current) return;
      if (!notificationRootRef.current.contains(event.target)) {
        setNotificationOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setNotificationOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [notificationOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
      }
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileMenuOpen]);

  async function markNotificationRead(id, options = {}) {
    if (!id) return;

    const { closeDropdown = false } = options;
    const notificationId = String(id);
    const wasUnread = notificationItems.some(
      (item) => String(item?._id || "") === notificationId && !item?.readAt
    );

    const previousItems = notificationItems;
    const previousUnread = Number(counters?.notificationsUnread || 0);

    try {
      setNotificationSavingId(notificationId);
      setNotificationItems((prev) =>
        prev.map((item) =>
          String(item?._id || "") === notificationId && !item?.readAt
            ? { ...item, readAt: new Date().toISOString() }
            : item
        )
      );
      if (wasUnread) {
        setCounters((prev) => ({
          ...prev,
          notificationsUnread: Math.max(0, Number(prev?.notificationsUnread || 0) - 1),
        }));
      }
      if (closeDropdown) {
        setNotificationOpen(false);
      }

      await api.post(`/notifications/${id}/read`);
      setNotificationError("");
    } catch {
      setNotificationItems(previousItems);
      setCounters((prev) => ({
        ...prev,
        notificationsUnread: previousUnread,
      }));
      setNotificationError("Melding bijwerken mislukt");
    } finally {
      setNotificationSavingId("");
    }
  }

  function handleHorizontalNavWheel(event) {
    const container = event.currentTarget;
    if (!container || container.scrollWidth <= container.clientWidth) return;

    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      container.scrollLeft += event.deltaY;
      event.preventDefault();
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white shadow-sm">
        <div className="px-4 py-3 md:px-6">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-base font-semibold sm:text-xl">{workspaceTitle}</h1>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 md:hidden"
                aria-label="Open navigatie"
                aria-expanded={mobileMenuOpen}
                aria-controls="pl-mobile-nav-drawer"
              >
                <Menu className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => navigate("/pl/weergave")}
                className="hidden rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 lg:inline-flex"
              >
                Weergave
              </button>
              <button
                type="button"
                onClick={() => navigate("/pl/weergave")}
                className="hidden h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 md:inline-flex lg:hidden"
                aria-label="Weergave"
                title="Weergave"
              >
                <Settings className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  logout?.();
                  navigate("/login");
                }}
                className="hidden rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 lg:inline-flex"
              >
                Uitloggen
              </button>
              <button
                type="button"
                onClick={() => {
                  logout?.();
                  navigate("/login");
                }}
                className="hidden h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 md:inline-flex lg:hidden"
                aria-label="Uitloggen"
                title="Uitloggen"
              >
                <LogOut className="h-4 w-4" />
              </button>

              <div className="relative" ref={notificationRootRef}>
                <button
                  type="button"
                  onClick={() => setNotificationOpen((prev) => !prev)}
                  className="relative rounded-lg border border-gray-200 bg-white p-2 hover:bg-gray-50"
                  aria-label="Meldingen"
                  aria-expanded={notificationOpen}
                >
                  <Bell className="h-5 w-5 text-gray-700" />
                  {counters.notificationsUnread > 0 ? (
                    <span className="absolute -right-1 -top-1 rounded-full bg-red-600 px-1.5 py-0.5 text-[9px] font-semibold leading-none text-white md:text-[10px]">
                      {counters.notificationsUnread > 99 ? "99+" : counters.notificationsUnread}
                    </span>
                  ) : null}
                </button>

                {notificationOpen ? (
                  <div className="absolute right-0 z-30 mt-2 w-[360px] max-w-[90vw] rounded-xl border border-gray-200 bg-white p-3 shadow-lg">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-900">Meldingen</p>
                      <button
                        type="button"
                        onClick={() => {
                          setNotificationOpen(false);
                          navigate("/pl/meldingen");
                        }}
                        className="text-xs text-blue-700 hover:underline"
                      >
                        Alles tonen
                      </button>
                    </div>

                    {notificationError ? (
                      <p className="mb-2 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                        {notificationError}
                      </p>
                    ) : null}

                    {notificationItems.length === 0 ? (
                      <p className="text-sm text-gray-500">Geen recente meldingen.</p>
                    ) : (
                      <div className="max-h-[320px] space-y-2 overflow-auto">
                        {notificationItems.map((item) => {
                          const unread = !item?.readAt;
                          return (
                            <article
                              key={item._id}
                              onClick={() => setNotificationOpen(false)}
                              className={`cursor-pointer rounded-lg border p-2 ${
                                unread ? "border-blue-200 bg-blue-50/40" : "border-gray-200 bg-white"
                              }`}
                            >
                              <p className="text-sm font-medium text-gray-900">{item?.title || "Melding"}</p>
                              <p className="mt-1 text-xs text-gray-700">{item?.message || "-"}</p>
                              <div className="mt-2 flex items-center justify-between gap-2">
                                <span className="text-[11px] text-gray-500">
                                  {formatDateTime(item?.createdAt)}
                                </span>
                                {unread ? (
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      markNotificationRead(item._id);
                                    }}
                                    disabled={notificationSavingId === String(item._id)}
                                    className="rounded border border-blue-300 bg-white px-2 py-1 text-[11px] font-medium text-blue-700 disabled:opacity-60"
                                  >
                                    {notificationSavingId === String(item._id) ? "..." : "Gelezen"}
                                  </button>
                                ) : null}
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {!sidebarNavigationLayout ? (
          <div className="hidden border-t border-gray-100 px-4 py-2 md:block md:px-4 lg:px-6">
            {navigationLayout === "topMegaMenu" ? (
              <TopMegaMenu
                groups={navigationGroups}
                location={location}
                onNavigate={(to) => navigate(to)}
              />
            ) : (
              <>
                <nav
                  aria-label="Hoofdmenu"
                  className="overflow-x-auto overscroll-x-contain scroll-smooth"
                  onWheel={handleHorizontalNavWheel}
                >
                  <div className="flex min-w-max items-center gap-2">
                    {navigationGroups.map((group) => {
                      const isActive = group.key === legacyGroupKey;
                      return (
                        <button
                          key={group.key}
                          type="button"
                          onClick={() => setLegacyGroupKey(group.key)}
                          className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition md:px-2.5 md:py-1 md:text-xs lg:px-3 lg:py-1.5 lg:text-sm ${
                            isActive
                              ? "bg-slate-800 text-white"
                              : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {group.label}
                        </button>
                      );
                    })}
                  </div>
                </nav>

                <div className="mt-2">
                  <nav
                    aria-label="Submenu"
                    className="overflow-x-auto overscroll-x-contain scroll-smooth"
                    onWheel={handleHorizontalNavWheel}
                  >
                    <div className="flex min-w-max items-center gap-2">
                      {(activeGroup?.items || []).map((item) => {
                        const active = item.to === activeSubItemTo;
                        const Icon = item.icon;
                        return (
                          <button
                            key={item.to}
                            type="button"
                            onClick={() => navigate(item.to)}
                            className={`inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm transition md:gap-1.5 md:px-2.5 md:py-1 md:text-xs lg:gap-2 lg:px-3 lg:py-1.5 lg:text-sm ${
                              active
                                ? "bg-blue-100 font-medium text-blue-800"
                                : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            {Icon ? <Icon className="h-4 w-4" /> : null}
                            <span className="truncate">{item.label}</span>
                            {item.badge ? (
                              <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold leading-none text-red-700 md:px-1.5 md:text-[10px] lg:px-2 lg:text-xs">
                                {item.badge}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </nav>
                  {activeGroupDescription ? (
                    <p className="mt-2 text-xs text-slate-500">{activeGroupDescription}</p>
                  ) : null}
                </div>
              </>
            )}
          </div>
        ) : null}
      </header>

      {mobileMenuOpen ? (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true" aria-label="Navigatie menu">
          <button
            type="button"
            aria-label="Sluit navigatie"
            onClick={() => setMobileMenuOpen(false)}
            className="absolute inset-0 bg-slate-900/45"
          />
          <aside
            id="pl-mobile-nav-drawer"
            className="absolute right-0 top-0 h-full w-[90vw] max-w-[360px] overflow-y-auto border-l border-slate-200 bg-white shadow-xl"
          >
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">Menu</p>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-700"
                  aria-label="Sluit menu"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-4 p-4">
              <section>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Hoofdmenu</p>
                <div className="grid grid-cols-2 gap-2">
                  {navigationGroups.map((group) => {
                    const isActive = group.key === legacyGroupKey;
                    return (
                      <button
                        key={group.key}
                        type="button"
                        onClick={() => setLegacyGroupKey(group.key)}
                        className={`rounded-lg border px-2.5 py-2 text-xs font-medium ${
                          isActive
                            ? "border-slate-800 bg-slate-800 text-white"
                            : "border-slate-200 bg-white text-slate-700"
                        }`}
                      >
                        {group.label}
                      </button>
                    );
                  })}
                </div>
              </section>

              <section>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Submenu</p>
                <div className="space-y-2">
                  {(activeGroup?.items || []).map((item) => {
                    const active = item.to === activeSubItemTo;
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.to}
                        type="button"
                        onClick={() => {
                          navigate(item.to);
                          setMobileMenuOpen(false);
                        }}
                        className={`flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left ${
                          active
                            ? "border-blue-200 bg-blue-50 text-blue-800"
                            : "border-slate-200 bg-white text-slate-700"
                        }`}
                      >
                        <span className="inline-flex min-w-0 items-center gap-2">
                          {Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
                          <span className="truncate text-sm">{item.label}</span>
                        </span>
                        {item.badge ? (
                          <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                            {item.badge}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </section>

              <section>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Acties</p>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => {
                      navigate("/pl/weergave");
                      setMobileMenuOpen(false);
                    }}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-left text-sm text-slate-700"
                  >
                    Weergave
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      logout?.();
                      navigate("/login");
                    }}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-left text-sm text-slate-700"
                  >
                    Uitloggen
                  </button>
                </div>
              </section>
            </div>
          </aside>
        </div>
      ) : null}

      <main className="px-4 pb-6 pt-4 md:px-6">
        <div className={sidebarNavigationLayout ? "lg:flex lg:items-start lg:gap-6" : ""}>
          {navigationLayout === "sideMenu" ? (
            <WorkspaceSideMenu
              groups={navigationGroups}
              location={location}
              activeGroupKey={legacyGroupKey}
              onGroupSelect={setLegacyGroupKey}
              onNavigate={(to) => navigate(to)}
            />
          ) : null}

          {navigationLayout === "compactIconMenu" ? (
            <WorkspaceCompactIconMenu
              groups={navigationGroups}
              location={location}
              activeGroup={activeGroup}
              activeGroupKey={legacyGroupKey}
              onGroupSelect={setLegacyGroupKey}
              onNavigate={(to) => navigate(to)}
            />
          ) : null}

          <div className="min-w-0 flex-1">
            <div className="pt-1">
              {breadcrumbs.length > 0 ? (
                <nav aria-label="Breadcrumb" className="mb-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  {breadcrumbs.map((crumb, index) => {
                    const isLast = index === breadcrumbs.length - 1;
                    return (
                      <div key={`${crumb.to}-${crumb.label}`} className="flex items-center gap-2">
                        {index > 0 ? <span className="text-slate-400">/</span> : null}
                        {isLast ? (
                          <span className="font-medium text-slate-700">{crumb.label}</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => navigate(crumb.to)}
                            className="rounded px-1 py-0.5 hover:bg-slate-100 hover:text-slate-800"
                          >
                            {crumb.label}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </nav>
              ) : null}
            </div>

            <div>
              <Outlet />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function normalizePath(path) {
  const value = String(path || "")
    .trim()
    .replace(/[?#].*$/, "");
  if (!value) return "";
  return value.length > 1 ? value.replace(/\/+$/, "") : value;
}

function buildPlatformBreadcrumbs(pathname) {
  const rawPath = normalizePath(pathname);
  const isProjectleiderScope = rawPath.startsWith("/pl");
  const isTenantAdminScope = rawPath.startsWith("/tenant-admin");
  if (!isProjectleiderScope && !isTenantAdminScope) return [];

  const normalizedPath = normalizeBreadcrumbPath(rawPath);
  const segments = normalizedPath.split("/").filter(Boolean);
  if (segments.length === 0) return [];

  const labelBySegment = {
    pl: "Projectleider",
    planning: "Planning",
    "inzet-capaciteit": "Inzet & capaciteit",
    projectweekoverzicht: "Projectweekoverzicht",
    projectcontrol: "Projectcontrol",
    uren: "Urenbeheer",
    uursoorten: "Uursoorten",
    calculaties: "Calculaties",
    projecten: "Projecten",
    monteurs: "Monteurs",
    mandagstaten: "Mandagstaten",
    "tenant-admin": "Bedrijfsinrichting",
    onboarding: "Onboarding",
    verlof: "Verlof",
    meldingen: "Meldingen",
    fotos: "Foto-overzicht",
    kennisbank: "Kennisbank",
    kam: "Veiligheid & Borging",
    analyse: "Analyse",
    toolboxen: "Toolboxen",
    "finance-safety": "Financieel & Controle",
    weergave: "Weergave",
    inkoop: "Inkoop",
    "externe-inzet": "Externe inzet",
    verwerking: "Verwerking",
    installaties: "Installaties",
    brandveiligheid: "Brandveiligheid",
    users: "Gebruikers",
    roles: "Rollen & rechten",
    settings: "Bedrijfsinstellingen",
    bedrijfsinrichting: "Algemeen",
    documenten: "Documenten",
    "huisstijl-documenten": "Huisstijl & documenten",
    "huistijl-documenten": "Huisstijl & documenten",
    status: "Inrichtingsstatus",
    modules: "Modules",
    workflows: "Werkprocessen",
    integrations: "Integraties",
    moneybird: "Moneybird",
    "technische-unie-oci": "Technische Unie / OCI",
    mailinstellingen: "Mailinstellingen",
    "pdf-templates": "PDF templates",
    "huisstijl-branding": "Huisstijl / branding",
    klantportaal: "Klantportaal",
  };

  const crumbs = [];
  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i];
    const to = `/${segments.slice(0, i + 1).join("/")}`;
    let label = labelBySegment[segment];

    if (!label) {
      const prev = segments[i - 1] || "";
      if (prev === "inkoop") label = "Order";
      else if (prev === "installaties") label = "Installatie";
      else if (prev === "modules") label = "Module";
      else if (prev === "workflows") label = "Workflow";
      else if (prev === "integrations") label = "Integratie";
      else label = segment.charAt(0).toUpperCase() + segment.slice(1);
    } else if (segment === "meldingen" && segments[i - 1] === "kam") {
      label = "Meldingen";
    }

    crumbs.push({ label, to });
  }

  if (isProjectleiderScope && crumbs.length === 1) {
    crumbs.push({ label: "Dashboard", to: "/pl" });
  }

  return crumbs;
}

function normalizeBreadcrumbPath(path) {
  if (path === "/pl/brandveiligheid") return "/pl/installaties";
  if (path.startsWith("/pl/brandveiligheid/installaties")) {
    return path.replace("/pl/brandveiligheid/installaties", "/pl/installaties");
  }
  if (path === "/tenant-admin/bedrijfsinrichting") return "/tenant-admin";
  if (path.startsWith("/tenant-admin/bedrijfsinrichting/huisstijl-documenten")) {
    return path.replace(
      "/tenant-admin/bedrijfsinrichting/huisstijl-documenten",
      "/tenant-admin/huisstijl-documenten"
    );
  }
  if (path.startsWith("/tenant-admin/bedrijfsinrichting/huistijl-documenten")) {
    return path.replace(
      "/tenant-admin/bedrijfsinrichting/huistijl-documenten",
      "/tenant-admin/huisstijl-documenten"
    );
  }
  if (path.startsWith("/tenant-admin/bedrijfsinrichting/documenten")) {
    return path.replace(
      "/tenant-admin/bedrijfsinrichting/documenten",
      "/tenant-admin/documenten"
    );
  }
  if (path === "/tenant-admin/rollen-rechten") return "/tenant-admin/roles";
  if (path === "/tenant-admin/werkprocessen") return "/tenant-admin/workflows";
  if (path === "/tenant-admin/monitoring") return "/tenant-admin/status";
  if (path === "/tenant-admin/pdf-templates") {
    return "/tenant-admin/integrations/pdf-templates";
  }
  if (path === "/tenant-admin/branding") {
    return "/tenant-admin/integrations/huisstijl-branding";
  }
  if (path === "/tenant-admin/customer-portal") {
    return "/tenant-admin/workflows/klantportaal";
  }
  return path;
}
